import React, { useEffect, useRef } from 'react';

import { Alert, BlockingAlert, Select, Skeleton } from 'design-system';
import { ReactComponent as AtencionIcon } from 'design-system/assets/atencion.svg';
import { useMediaQuery } from 'react-responsive';
import { useHistory } from 'react-router-dom';
import { pushGtmEvent } from 'analytics/gtm';

import { useCustomerStatusOnEnter } from 'hooks/useCustomerStatus';
import { buildReasonsTagLabel } from 'utils/blockReasons';

import { useBases } from '../hooks/useBases';
import { REFUEL_MESSAGES } from '../constants';

import {
  AlertMessage,
  BaseWrap,
  BlockedCta,
  Container,
  Divider,
  FieldLabel,
  ForecastChip,
  ForecastSkeleton,
  ForecastWrap,
  PreviewData,
  SelectWrap,
  TopRow,
} from './styles';

const PreviewRefuel: React.FC = () => {
  const history = useHistory();
  const {
    getBases,
    isLoading,
    bases,
    selectedBase,
    addSelectedBase,
    isUniqueBase,
    nextRefuelDate,
  } = useBases();

  const {
    reasons,
    destination,
    isLoading: isCheckingStatus,
  } = useCustomerStatusOnEnter();

  const isBlocked = reasons.length > 0;
  const viewEventSent = useRef('');

  useEffect(() => {
    getBases();
  }, [getBases]);

  useEffect(() => {
    if (!isBlocked) return;

    const label = buildReasonsTagLabel(reasons);
    if (viewEventSent.current === label) return;

    viewEventSent.current = label;
    pushGtmEvent({
      event: 'view_content',
      eventCategory: 'home',
      eventAction: 'view',
      eventLabel: label,
      pageSection: 'home',
      elementType: 'message',
    });
  }, [isBlocked, reasons]);

  const handleBlockedCta = (): void => {
    pushGtmEvent({
      event: 'click_cta',
      eventCategory: 'home',
      eventAction: 'click',
      eventLabel: REFUEL_MESSAGES.BLOCKED_CTA,
      pageSection: 'home',
      elementType: 'button',
    });

    history.push(
      destination === 'bases' ? '/pedido-abastecimento' : '/pendencias'
    );
  };

  const alertContent = (): React.ReactNode => {
    if (isBlocked) {
      return (
        <AlertMessage
          dangerouslySetInnerHTML={{ __html: REFUEL_MESSAGES.BLOCKED }}
        />
      );
    }

    if (!selectedBase) {
      return REFUEL_MESSAGES.SELECT_BASE;
    }

    if (nextRefuelDate) {
      return (
        <AlertMessage
          dangerouslySetInnerHTML={{
            __html: REFUEL_MESSAGES.PREVIEW_DESCRIPTION,
          }}
        />
      );
    }

    return REFUEL_MESSAGES.UNABLE_TO_CALCULATE;
  };

  const showForecast = isBlocked || Boolean(nextRefuelDate);

  return (
    <Container>
      {!isLoading && bases?.length > 0 && (
        <PreviewData>
          <TopRow>
            <BaseWrap>
              <FieldLabel htmlFor="local-abastecimento">
                {'Central de abastecimento '}
                <span>*</span>
              </FieldLabel>
              <SelectWrap>
                <Select
                  id="local-abastecimento"
                  fullWidth
                  disabled={isUniqueBase}
                  borderColor="#262626"
                  color="#262626"
                  value={selectedBase?.name || ''}
                  onChange={(event) => {
                    const v = event.currentTarget.value;
                    pushGtmEvent({
                      event: 'click_cta',
                      eventCategory: 'home',
                      eventAction: 'click',
                      eventLabel: v || 'Central de abastecimento',
                      pageSection: 'home',
                      elementType: 'select',
                    });
                    addSelectedBase(v);
                  }}
                >
                  {!selectedBase && <option value="">Selecione</option>}
                  {bases.map((base) => (
                    <option key={base.name} value={base.name}>
                      {base.nickname}
                    </option>
                  ))}
                </Select>
              </SelectWrap>
            </BaseWrap>

            {isCheckingStatus && (
              <ForecastWrap>
                <ForecastSkeleton
                  height={36}
                  label="Verificando a previsão do próximo abastecimento"
                />
              </ForecastWrap>
            )}

            {!isCheckingStatus && showForecast && (
              <ForecastWrap>
                <ForecastChip>
                  {'Previsão do próximo abastecimento: '}
                  <strong>
                    {isBlocked
                      ? REFUEL_MESSAGES.UNAVAILABLE_LABEL
                      : nextRefuelDate}
                  </strong>
                </ForecastChip>
              </ForecastWrap>
            )}
          </TopRow>

          <Divider />

          {isCheckingStatus && <Skeleton height={40} />}

          {!isCheckingStatus && isBlocked && (
            <BlockingAlert>{alertContent()}</BlockingAlert>
          )}

          {!isCheckingStatus && !isBlocked && (
            <Alert
              variant="warning"
              icon={<AtencionIcon />}
              backgroundColor="#FFF8E6"
              borderColor="#F8C953"
              color="#575757"
            >
              {alertContent()}
            </Alert>
          )}

          {!isCheckingStatus && isBlocked && (
            <BlockedCta type="button" onClick={handleBlockedCta}>
              {REFUEL_MESSAGES.BLOCKED_CTA}
            </BlockedCta>
          )}
        </PreviewData>
      )}
    </Container>
  );
};

export default PreviewRefuel;
